import { companyCellStyles } from "../styles/shared/companyCellStyles";
import React, { useEffect, useState } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import { resolvePublicAssetUrl } from "../utils/apiUrls";
import { loadCompanyTableIdentity } from "../utils/companyTableIdentity";

const text = (value) => typeof value === "string" ? value.trim() : "";

export function PipelineCompanyTableCell({ row = {} }) {
  const assignment = row.job_assignments?.find(job => row.job_id != null && String(job.id) === String(row.job_id));
  return <CompanyTableCell
    row={{ company_id: row.job_company_id || assignment?.company_id }}
    name={row.job_company_name || assignment?.company_name}
    logo={row.job_company_logo || assignment?.company_logo || ""}
    website={row.job_company_website || assignment?.company_website || ""}
  />;
}

export function companyDomain(value) {
  const raw = text(value);
  if (!raw || raw === "-" || raw === "—") return "";
  try {
    const url = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    return /^(https?:)$/.test(url.protocol) ? url.hostname.replace(/^www\./i, "") : "";
  } catch {
    return "";
  }
}

// Only company-specific fields are used for related records: a contact's
// personal website or avatar must never become its employer's identity.
export default function CompanyTableCell({
  row = {}, companyEntity = false, name, logo, website, onClick, href,
  websiteHref, children }) {
  const company = typeof row.company === "object" && row.company ? row.company : {};
  const label = text(name) || (companyEntity ? text(row.name) : "") || text(row.current_company) || text(row.currentCompany) ||
    text(row.candidate_company) || text(row.linked_company_name) || text(row.company_name) ||
    text(row.companyName) || text(row.entity_name) || text(row.company) || text(company.name) || "—";
  const savedImage = logo ?? (row.current_company_logo || row.linked_company_logo || row.company_logo || row.company_logo_url ||
    row.companyLogo || row.entity_logo || company.logo || company.company_logo ||
    (companyEntity ? row.logo : ""));
  const savedDomain = companyDomain(website || row.current_company_website || row.linked_company_website ||
    row.company_website || row.companyWebsite || row.entity_website || company.website ||
    row.company_website_url || row.company_url || company.company_website || company.website_url ||
    (companyEntity ? row.website || row.website_url : ""));
  const companyId = row.current_company_id || row.linked_company_id || row.company_id || row.companyId || company.id ||
    (companyEntity ? row.id : null);
  const identityKey = `${companyId || ""}:${label}`;
  const [resolved, setResolved] = useState(null);
  useEffect(() => {
    if ((savedDomain && savedImage) || !label || label === "—" || label === "-") return;
    let active = true;
    loadCompanyTableIdentity({ id: companyId, name: label }).then(company => {
      if (active) setResolved({ key: identityKey, company });
    });
    return () => { active = false; };
  }, [companyId, identityKey, label, savedDomain, savedImage]);
  const resolvedCompany = resolved?.key === identityKey ? resolved.company : null;
  const domain = savedDomain || companyDomain(resolvedCompany?.website);
  const image = savedImage || resolvedCompany?.logo;
  const interactive = Boolean(onClick || href);
  return (
    <Box data-company-cell sx={companyCellStyles.root()}>
      <Avatar src={image ? resolvePublicAssetUrl(image) : undefined} alt="" imgProps={{ style: companyCellStyles.logoImage() }}
        sx={companyCellStyles.avatar()}>
        {label === "—" ? "—" : label.charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={companyCellStyles.text()}>
        <Typography component={href ? "a" : onClick ? "button" : "span"}
          {...(href ? { href, target: "_blank", rel: "noreferrer" } : onClick ? { type: "button" } : {})}
          onClick={onClick} title={label} noWrap data-company-text
          sx={companyCellStyles.name({ interactive })}>
          {label}
        </Typography>
        <Typography component={websiteHref && domain ? "a" : "span"}
          {...(websiteHref && domain ? { href: websiteHref, target: "_blank", rel: "noreferrer" } : {})}
          title={domain || "Website unavailable"} noWrap data-company-text
          sx={companyCellStyles.website()}>
          {domain || "—"}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}
